import React, { forwardRef, useEffect, useImperativeHandle } from 'react'
import { useTranslation } from 'react-i18next'
import { FieldErrors, FormProvider } from 'react-hook-form'
import { toast } from 'react-toastify'
import reportApiFailure from 'shared/api/reportApiFailure'
import { HandleSubmitResult } from 'features/declaration/form/components/TransitFormTabHandle'
import { useFetchDirectoEvent } from '../api/fetchDirectoEvent'
import { toDirectoFormValues } from '../models/mapper'
import { ErrorResult } from '../../../../../shared/models/types'
import { DirectoFormValues } from '../models/schema'
import { DirectoEvent } from '../models/types'
import FormSelect from '../../../../../components/p5/FormSelect'
import FormRow from '../../../../../components/FormRow'
import Input from '../../../../../components/p5/Input'
import useDirectoCustomerSearch from '../../../../../routes/Declaration/DeclarationInfo/DirectoEvent/hooks/useDirectoCustomerSearch'
import { DirectoEventResponseStatusEnum } from '../../../../../api/models/directo-event-response'
import useSaveDirectoForm from './useSaveDirectoForm'
import useDirectoForm from './useDirectoForm'

export type DirectoEventSaveResult =
  { success: true; data: DirectoEvent }
  | { success: false; formErrors?: FieldErrors<DirectoFormValues>; errors?: ErrorResult[] }

export interface DirectoFormHandle {
  save: () => Promise<DirectoEventSaveResult>
  isDirty: () => boolean
  isEnabled: () => boolean
}

interface DirectoFormProps {
  transitOperationId: number
  directoEventId: number | null
}

const DirectoForm = forwardRef<DirectoFormHandle, DirectoFormProps>(({
  transitOperationId,
  directoEventId,
} : DirectoFormProps, ref) => {
  const { t } = useTranslation()

  const { data: directoEvent } = useFetchDirectoEvent(directoEventId, transitOperationId)
  const { saveDirectoFormValues } = useSaveDirectoForm(directoEvent?.id ?? null, transitOperationId)

  const { form } = useDirectoForm()
  const {
    reset, getValues, handleSubmit, formState: { isDirty, isSubmitting },
  } = form

  const directoEventStatus = directoEvent?.status ?? DirectoEventResponseStatusEnum.IDLE
  const isDisabled = directoEventStatus !== DirectoEventResponseStatusEnum.IDLE

  const {
    loadOptions,
    valueBuffer,
    setValueBuffer,
    directoClients,
  } = useDirectoCustomerSearch({ initialCustomerId: directoEvent?.customerId ?? null })

  useEffect(() => {
    if (!directoEvent) return
    reset({
      ...structuredClone(getValues()),
      ...toDirectoFormValues(directoEvent),
    })
  }, [directoEvent, getValues, reset])

  const validateAndGet = () => new Promise<HandleSubmitResult<DirectoFormValues>>((resolve) => {
    handleSubmit(
      (data) => {
        resolve({ success: true, data })
      },
      (errors) => {
        resolve({ success: false, errors })
      },
    )()
  })

  const save = async (): Promise<DirectoEventSaveResult> => {
    if (isDisabled || isSubmitting) {
      return { success: false, errors: [{ message: 'Form submission is disabled' }] }
    }

    const result = await validateAndGet()
    if (!result.success) {
      console.error('Directo Form errors', result.errors)
      return { success: false }
    }

    if (!result.data) {
      toast.error(t('directo.saveError'))
      return { success: false, errors: [{ message: 'Data missing' }] }
    }

    try {
      const saved = await saveDirectoFormValues({
        values: result.data,
      })
      toast.success('Directo event saved')
      return { success: true, data: saved }
    } catch (error) {
      reportApiFailure(error, () => toast.error(t('directo.saveError')))
      return { success: false, errors: [{ message: 'Error occurred when saving directo event' }] }
    }
  }

  useImperativeHandle(ref, () => ({
    save,
    isDirty: () => isDirty,
    isEnabled: () => !isDisabled,
  }))

  const handleFormSubmit: React.FormEventHandler<HTMLFormElement> = (e) => {
    e.preventDefault()
    // eslint-disable-next-line no-void
    void save()
  }

  return (
    <FormProvider {...form}>
      <form onSubmit={handleFormSubmit}>
        <FormRow title={t('directo.customer')}>
          <FormSelect
            <DirectoFormValues>
            field="customerId"
            labelKey="directo.customer"
            type="async"
            loadOptions={loadOptions}
            valueBuffer={valueBuffer}
            setValueBuffer={setValueBuffer}
            isDisabled={isDisabled}
          />
        </FormRow>
        <FormRow title={t('directo.descriptionAddendum')}>
          <Input
            <DirectoFormValues>
            field="descriptionAddendum"
            labelKey="directo.descriptionAddendum"
            type="textarea"
            placeholder={t('common.fieldPlaceholder')}
            disabled={isDisabled}
          />
        </FormRow>
        <FormRow title={t('customers.notes')}>
          <textarea
            className="form-control"
            disabled
            rows={5}
            placeholder={t('common.fieldPlaceholder')}
            value={directoClients.find((client) => client.id === directoEvent?.customerId)?.note ?? ''}
          />
        </FormRow>
        <div className="col-lg-12 d-flex justify-content-end align-items-center">
          <button
            type="submit"
            disabled={isDisabled}
          >
            Save changes
          </button>
        </div>
      </form>
    </FormProvider>
  )
})

export default DirectoForm
